/**
 * ★ AS REMISSÕES do canal falado — [spec-008], D-13/N16, revisto pela
 * **D-O-3** ([T044]). Nono irmão de `transporte-tempo-real.js`, extraído do
 * `-delegacao.js` quando ele bateu no teto de 500 linhas ([T040b]).
 *
 * A divisão é por responsabilidade: aqui está **o que a voz pode dizer** — um
 * conjunto FECHADO de saídas — e a guarda que o torna verificável; lá ficou a
 * POLÍTICA (quando cada uma é usada).
 *
 * ## ★ Por que o IDENTIFICADOR continua fora da fala
 *
 * O G-11 mediu 11 amostras do canal falado: ele **parafraseia a forma 11/11**,
 * **não altera o dígito 0/11** e **altera ou omite o IDENTIFICADOR 6/11** —
 * `NTN-B 2035` virou `NTN-B`, mantendo o número e apagando o discriminante. Um
 * valor correto atribuído ao papel errado é pior que um valor errado, porque o
 * primeiro parece confiável.
 *
 * ★ **[T072, 18/09/2026] O que isso proíbe é DEFORMAR, não dizer.** O owner
 * decidiu que a voz PODE falar nome e dados do cliente — *"é esperado que a
 * plataforma realmente fale esses dados (até pq o usuario do outro lado vai
 * estar falando)"*: o consultor diz o nome em voz alta para perguntar, então o
 * sigilo ambiental nunca dependeu da voz da plataforma. Quem consolida o texto
 * da tela é `montarSaidaDeConsolidacao` (irmão `-consolidacao.js`), e ele
 * **permite** o identificador e **proíbe alterá-lo**.
 *
 * ⚠️ Nas saídas CONGELADAS abaixo o "não diga" virou **"não invente"**, que é
 * o fato: elas não carregam conteúdo nenhum: a voz não recebeu o dado, e o que
 * ela não pode é preencher a lacuna de cabeça. O valor exato segue no CARTÃO,
 * que é a fonte auditável (G-14: o áudio diverge do transcript 2/31).
 *
 * ## ★ [T044] O que a D-O-3 mudou: o NÚMERO passa a ser falado
 *
 * O owner decidiu em 2026-09-17 que a voz **fala** o valor — *"arredondado
 * mesmo, com no máximo 3 dígitos significativos"*. E isso **aperta** a guarda
 * em vez de afrouxá-la, porque muda **quem arredonda**:
 *
 * | | antes (v1, medido) | agora |
 * |---|---|---|
 * | quem arredonda | **o modelo**: `1308814.05` virou *"aproximadamente 1,3 milhão"* em **2 de 4** execuções, e nas outras 2 não | **nós**, em `formatar-valor-para-voz.js` |
 * | verificável | não | **sim**: `conferirQueRemete` recalcula e exige igualdade |
 *
 * Então a lista branca deixa de ser *"nenhum dígito atravessa"* e passa a ser
 * *"o único número que atravessa é o que o NOSSO formatador produziu"* — o que
 * é uma régua mais forte, porque cobre também o arredondamento.
 *
 * ⚠️ O que **nenhum** teste daqui cobre: o G-14 mediu que o áudio pode
 * divergir do próprio transcript (2 em 31 sessões, uma delas num dígito de
 * percentual, 4,2 → 4,4). Arredondar **encolhe o dano**, não o elimina — e é
 * por isso que o valor EXATO continua no cartão, que é a fonte auditável.
 *
 * `instrucao_de_fala` é direção de comportamento, não texto a ser lido — o
 * modelo reformula em PT-BR falado (o que ele faz com todo `output`, G-12 §6).
 * A ÚNICA exceção é `valor_falado`, que é para ser dito como está.
 */
import { formatarParaFala, TIPO_NUMERO } from './formatar-valor-para-voz'
import {
  CHAVE_DA_RESPOSTA_NA_TELA,
  clausulaDoNumeroFalado,
  conferirAConsolidacao,
  conferirAFalaDeConsolidacao,
  PREFIXO_DA_FALA_DE_DESFECHO,
} from './transporte-tempo-real-consolidacao'
import {
  ErroDeVozTempoReal,
  EVENTO_APPEND_DE_COMENTARIO,
  MOTIVO_SAIDA_RECITA_DADO,
} from './transporte-tempo-real-protocolo'

// ★ [T072] A consolidação (o desfecho que LEVA o texto da tela) mora no irmão
// `transporte-tempo-real-consolidacao.js` — este arquivo fechou em 448 linhas
// e o teto do repo é 500. Reexportada para quem consome as remissões não
// precisar saber disso: é a mesma cortesia que a delegação faz com este.
export * from './transporte-tempo-real-consolidacao'

/**
 * As remissões, uma por desfecho. São constantes congeladas, sem nenhuma
 * interpolação do resultado: é por CONSTRUÇÃO que o cliente, o ativo, o
 * período e a data-base não atravessam.
 *
 * ★ [T044] `SAIDA_PRONTA` é o desfecho pronto **SEM número legível** — a tool
 * não devolveu valor, ou devolveu um que o formatador não sabe ler. Ela é o
 * comportamento anterior à D-O-3 e continua sendo o caminho seguro: a voz
 * remete, e o cartão mostra. O desfecho pronto **COM** número é
 * `montarSaidaComValor`, mais abaixo.
 *
 * ⚠️ **[T072] Desde a D-O-6 nenhum caminho do desfecho a escolhe.** Turno com
 * texto sai por `montarSaidaDeConsolidacao` (o irmão `-consolidacao.js`);
 * turno com número e sem texto, por `montarSaidaComValor`; turno sem nada, por
 * `SAIDA_DE_FALHA`. Ela **fica** porque a lista branca é o vocabulário FECHADO
 * do canal falado: tirar uma saída de lá muda o contrato do que a voz pode
 * dizer, e essa não é a mudança que a D-O-6 pediu. Quem precisar de uma
 * remissão pura — um caminho novo que anuncie sem conteúdo — encontra esta
 * pronta e já coberta.
 *
 * `instrucao_de_fala` é direção de comportamento, não texto a ser lido — o
 * modelo reformula em PT-BR falado (é o que ele faz com todo `output`, medido
 * no G-12 §6).
 */
export const SAIDA_PRONTA = Object.freeze({
  status: 'pronto',
  onde: 'cartao_da_conversa',
  instrucao_de_fala:
    'A consulta terminou e o resultado já está no cartão desta conversa. ' +
    'Avise o consultor e ofereça seguir a partir do cartão. Você NÃO recebeu ' +
    'o conteúdo do resultado: não invente valores, nomes de cliente, nomes de ' +
    'ativo, datas nem períodos — eles estão na tela.',
})

/**
 * ★ [T073] O laço fecha ANTES de a consulta terminar — "dois tempos".
 *
 * Esta saída existe por um fato medido: **enquanto a delegação está aberta, o
 * provedor engole tudo o que empurramos** (17/09: 3 sondagens × `commentary.
 * append`/`instructions.append`, 0 ecos, 0 deltas) e **não verbaliza o próprio
 * `function_call_output`** (14/14 execuções da sonda). Com a sessão OCIOSA, as
 * MESMAS 3 sondagens falam (7-8 deltas cada, ecoadas).
 *
 * Então o laço é fechado assim que a consulta parte, com esta remissão, e a
 * resposta chega depois pelo canal que funciona — o append da consolidação.
 *
 * ⚠️ Ela NÃO carrega dado nenhum, e não pode: a consulta mal começou. O que
 * ela autoriza é dizer que está em andamento.
 */
export const SAIDA_EM_ANDAMENTO = Object.freeze({
  status: 'em_andamento',
  onde: 'cartao_da_conversa',
  instrucao_de_fala:
    'A consulta foi disparada e ainda está em andamento. Diga ao consultor ' +
    'que você já está buscando e que responde em instantes. Você NÃO recebeu ' +
    'resultado nenhum: não invente valores, nomes de cliente, nomes de ativo, ' +
    'datas nem períodos.',
})

export const SAIDA_DE_FALHA = Object.freeze({
  status: 'falhou',
  onde: 'cartao_da_conversa',
  instrucao_de_fala:
    'A consulta não pôde ser concluída agora. Diga isso ao consultor e peça ' +
    'para ele tentar de novo em instantes. NÃO invente nenhum dado.',
})

export const SAIDA_DE_RECUSA_POR_CONCORRENCIA = Object.freeze({
  status: 'recusado',
  onde: 'cartao_da_conversa',
  instrucao_de_fala:
    'Já existe uma consulta em andamento nesta conversa. Diga ao consultor ' +
    'que esta segunda vai ser feita depois que a primeira responder.',
})

export const SAIDA_SEM_TURNO = Object.freeze({
  status: 'indefinido',
  onde: 'cartao_da_conversa',
  instrucao_de_fala:
    'Não ficou claro o que foi pedido. Peça ao consultor que repita o pedido ' +
    'com o nome do cliente. NÃO adivinhe o cliente nem a data.',
})

/**
 * ★ [T040b] N21 — o identificador da demanda não corresponde ao que o consultor
 * falou. A voz PERGUNTA, e **não sugere nome nenhum**: repetir o palpite
 * (`"você disse Teste Alpha?"`) poria na cabeça do consultor exatamente o nome
 * alterado que esta guarda existe para barrar (N16).
 */
export const SAIDA_PEDE_IDENTIFICADOR = Object.freeze({
  status: 'indefinido',
  onde: 'cartao_da_conversa',
  instrucao_de_fala:
    'O nome que chegou não corresponde ao que o consultor falou. Peça que ele ' +
    'repita o nome, soletrando se precisar. NÃO sugira nomes, NÃO adivinhe e ' +
    'NÃO diga o nome que você supôs.',
})

/**
 * ★ [T033] O pedido falado de ESCRITA — a decisão espera na TELA (US-5).
 *
 * A escrita produz um CARTÃO com pré-visualização, e é lá que ela se consuma.
 * A voz diz que existe decisão esperando, diz **onde**, e não pede confirmação
 * falada: nenhuma palavra dita consuma escrita (AC1 da US-5, garantido no
 * SERVIDOR pela origem do turno — aqui a voz nem chega a oferecer).
 *
 * O `idProposta` NÃO entra nesta saída, de propósito: ele é identificador de
 * negócio, o canal falado altera identificador em 6/11 (G-11), e a tela já o
 * recebe por `propostaAguardando`.
 */
export const SAIDA_DECISAO_NA_TELA = Object.freeze({
  status: 'decisao_na_tela',
  onde: 'cartao_da_conversa',
  instrucao_de_fala:
    'A ação foi preparada e está esperando a decisão do consultor no cartão ' +
    'desta conversa. Diga que ele precisa revisar e confirmar NA TELA. NÃO ' +
    'peça confirmação por voz e NÃO diga que já fez. Você não recebeu o ' +
    'conteúdo da proposta: não invente valores, nomes nem datas — eles estão ' +
    'no cartão, que é onde ele decide.',
})

// A lista branca é o que torna a guarda de D-13 verificável em vez de
// combinada: a saída enviada tem de ser IDÊNTICA a uma destas.
const _SAIDAS_PERMITIDAS = [
  SAIDA_PRONTA,
  SAIDA_EM_ANDAMENTO,
  SAIDA_DE_FALHA,
  SAIDA_DE_RECUSA_POR_CONCORRENCIA,
  SAIDA_SEM_TURNO,
  SAIDA_PEDE_IDENTIFICADOR,
  SAIDA_DECISAO_NA_TELA,
]
const _SAIDAS_SERIALIZADAS = _SAIDAS_PERMITIDAS.map((saida) =>
  JSON.stringify(saida)
)

/**
 * ★ [T044] O VALOR que a voz pode falar, lido do que a tool devolveu — D-O-3.
 *
 * Mora aqui, e não na delegação, pelo mesmo critério que separou os dois
 * arquivos: este módulo é dono do caminho do dado até a fala — **ler**,
 * formatar, montar e conferir —, e a delegação é dona da política.
 *
 * `valor_para_voz` é um nome só, novo e explícito. `lerPropostaDoResultado`
 * aceita duas grafias porque as duas já existiam no repositório (`preview_id`
 * do Modo Chat, `id_proposta` do Modo Agente); aqui não existe nenhuma, e
 * inventar apelidos para um campo sem produtor é criar a terceira grafia antes
 * da primeira.
 *
 * ★ **[T045] O produtor existe desde 17/09/2026:** é
 * `contexto-de-voz-tempo-real.js::lerDesfechoDoTurnoFalado`, que lê do store do
 * chat o cartão `card` do dashboard — a única forma de resultado da plataforma
 * que declara, por contrato, UM valor e o TIPO dele (`options.value` +
 * `options.format`). O `delegar` de `view-chat.vue` devolve o objeto inteiro.
 *
 * ⚠️ **Divergência declarada, a mesma da proposta:** o contrato diz
 * `delegar: (texto) => AsyncIterable<string>` (`contracts/transporte.md`), e
 * string não carrega valor nenhum — o caminho de string continua caindo em
 * `SAIDA_PRONTA`, e é assim que ele deve ficar. Quando não há UM valor com
 * tipo declarado, as chaves nem chegam aqui: a voz remete, que é o
 * comportamento anterior à D-O-3 e é seguro. Nada inventa valor quando ele
 * não veio.
 *
 * @returns {?{valor: *, tipo: string}} o que a TOOL devolveu, CRU — quem
 *   arredonda é `formatarParaFala`; quem confere é `conferirQueRemete`.
 */
export const lerValorDoResultado = (resultado) => {
  if (!resultado || typeof resultado !== 'object') return null
  const valor = resultado.valor_para_voz
  if (valor === null || valor === undefined) return null
  return { valor, tipo: String(resultado.tipo_do_valor || TIPO_NUMERO) }
}

/**
 * ★ [T044] A chave — e a ÚNICA — por onde um dado de negócio atravessa para a
 * fala. Nomeada porque a guarda a procura pelo nome: é ela que decide se a
 * saída é julgada pela lista branca ou pelo formatador.
 */
export const CHAVE_DO_VALOR_FALADO = 'valor_falado'

/**
 * ★ [T044] O desfecho pronto **com** o número — D-O-3.
 *
 * `valor_falado` é a única parte de todo este módulo que o modelo deve repetir
 * literalmente, e a `instrucao_de_fala` diz isso com todas as letras: o G-11
 * mediu que ele **parafraseia a forma 11/11** (o que aqui é desejável) e **não
 * altera o dígito 0/11** (o que é o que torna isto viável). O que ele altera é
 * o IDENTIFICADOR (6/11) — que por isso continua fora.
 *
 * Quem monta NÃO escolhe o texto do número: ele vem de `formatarParaFala`, e a
 * guarda abaixo refaz a conta. Montar à mão com outro arredondamento é
 * exatamente o que fica vermelho.
 */
export const montarSaidaComValor = ({ valorFalado }) =>
  Object.freeze({
    status: 'pronto',
    onde: 'cartao_da_conversa',
    valor_falado: String(valorFalado),
    instrucao_de_fala:
      'A consulta terminou. Diga ao consultor o número que está em ' +
      '`valor_falado`, EXATAMENTE como ele está: não arredonde de novo, não ' +
      'acrescente casas, não troque a escala e não diga "aproximadamente". ' +
      'O resultado completo está no cartão desta conversa: remeta a ele para ' +
      'o detalhe. Este número é a ÚNICA coisa que você recebeu: não invente ' +
      'nomes de cliente, nomes de ativo, datas, períodos nem nenhum OUTRO ' +
      'número — esses estão na tela.',
  })

/**
 * ★ [T044] O invariante da D-O-3, em código:
 *
 * > **O que a voz fala é exatamente a saída do formatador de 3 dígitos
 * > significativos, aplicada ao valor que a tool devolveu.**
 *
 * A guarda o verifica do jeito mais direto que existe: ela **refaz a conta** a
 * partir da origem e exige que a saída inteira seja idêntica à que sairia
 * daqui. Uma comparação cobre as cinco maneiras de violar o contrato —
 * arredondar diferente, mandar o valor exato, trocar a escala, enxertar um
 * campo (`cliente`) ou reescrever a `instrucao_de_fala`.
 *
 * ⚠️ `origem` ausente com `valor_falado` presente é VERMELHO, e este é o caso
 * que mais importa: um número na fala sem origem contra o que conferir é
 * precisamente o número que veio de lugar nenhum.
 */
const _conferirOValorFalado = (saida, origem) => {
  const pedido = origem || {}
  const falado = formatarParaFala(pedido.valor, { tipo: pedido.tipo })
  if (falado) {
    const esperada = JSON.stringify(montarSaidaComValor({ valorFalado: falado }))
    if (JSON.stringify(saida) === esperada) return
  }
  throw new ErroDeVozTempoReal(
    MOTIVO_SAIDA_RECITA_DADO,
    'O valor falado não é o que o formatador de 3 dígitos significativos ' +
      'produz para o valor que a tool devolveu: quem arredonda somos nós.'
  )
}

/**
 * ★ A guarda de D-13/D-O-3/D-O-6, no ÚNICO ponto por onde uma saída chega ao
 * canal.
 *
 * TRÊS regimes, e a saída escolhe qual pela chave que carrega: **com**
 * `resposta_na_tela`, ela é conferida contra o texto do turno (o irmão
 * `-consolidacao.js`); **com** `valor_falado`, contra o formatador (acima);
 * **sem** nenhuma das duas, contra a lista branca.
 *
 * A ordem entre os dois primeiros não é arbitrária: a consolidação PODE
 * carregar `valor_falado` junto — quando o turno teve texto e um valor com
 * tipo, a voz resume a prosa e diz o número que nós arredondamos. Julgá-la
 * pelo regime do número sozinho a reprovaria pelo campo a mais, que é
 * legítimo.
 *
 * A lista branca é lista branca de propósito. Um teste de "não contém dígito"
 * deixaria passar `{"cliente":"Teste Alfa"}`, e um de "não contém o resultado"
 * só pega o que já se sabe procurar. O que se quer impedir é a mudança natural
 * que um dev futuro faz quando lhe pedem "faz a voz falar o valor": interpolar
 * o resultado na saída. Depois da [T044] há UM jeito autorizado de fazer isso
 * — `montarSaidaComValor` — e qualquer outro continua deixando de ser idêntico
 * a uma das seis.
 *
 * @param {Object} saida a que vai ao `function_call_output`
 * @param {Object} [origem] `{texto, valor, tipo}` — o que o TURNO devolveu,
 *   não o que a saída diz. Obrigatória quando a saída carrega `valor_falado`
 *   ou `resposta_na_tela`.
 * @throws {ErroDeVozTempoReal} `MOTIVO_SAIDA_RECITA_DADO`
 */
export const conferirQueRemete = (saida, origem) => {
  const ehObjeto = Boolean(saida) && typeof saida === 'object'
  if (
    ehObjeto &&
    Object.prototype.hasOwnProperty.call(saida, CHAVE_DA_RESPOSTA_NA_TELA)
  ) {
    conferirAConsolidacao(saida, origem)
    return
  }
  if (
    ehObjeto &&
    Object.prototype.hasOwnProperty.call(saida, CHAVE_DO_VALOR_FALADO)
  ) {
    _conferirOValorFalado(saida, origem)
    return
  }
  if (_SAIDAS_SERIALIZADAS.indexOf(JSON.stringify(saida)) !== -1) return
  throw new ErroDeVozTempoReal(
    MOTIVO_SAIDA_RECITA_DADO,
    'A saída do canal falado não é uma das remissões permitidas: o dado de ' +
      'negócio vai ao cartão, não à fala.'
  )
}


// ── [T033] A guarda de D-13 no OUTRO caminho: a fala empurrada ───────────

/**
 * ★ [T063] O ACOMPANHAMENTO da espera — as frases que a voz diz ENQUANTO a
 * consulta corre, e que são as únicas que podem ser EMPURRADAS ao canal
 * falado (`commentary.append`).
 *
 * ## ⚠️ Esta lista estava VAZIA, e a justificativa daí lia o G-13 errado
 *
 * O comentário que vivia aqui dizia: *"o modelo conversa sozinho durante a
 * espera (9/9 no G-13), então empurrar é opção, não necessidade"*. As 9/9 do
 * G-13 §1 são **respostas a interpelações** — o método do portal é, literal,
 * *"delegação aberta, nenhuma resposta enviada, **e eu falando por cima**"*.
 * Elas provam que o modelo RESPONDE quando o consultor fala na espera; não
 * provam, e nunca mediram, que ele **começa** turno sozinho.
 *
 * O que foi medido num navegador de verdade, com o corpo que o nosso backend
 * monta e com as instruções da [T048] ativas ([T050],
 * `~/aawz-tmp/spec008-t050/eventos-browser.json`), é o oposto: o modelo
 * anuncia UMA vez (`" Certo, já tô consultando essa informação pra você."`,
 * 12,69 s → 14,83 s) e depois fica **31,1 s em silêncio absoluto** com a
 * delegação pendente — zero `session.output_transcript.delta` entre 14,83 s e
 * 45,90 s, com o canal vivo (os três `session.usage.updated` do ledger são os
 * únicos eventos da janela). Ninguém interpelou, porque é isso que o consultor
 * faz: ele pergunta e espera. É exatamente a queixa do owner — *"a voz só
 * falou no final"*.
 *
 * ## A cadência é MEDIDA, e o G-13 §4 é quem diz que empurrar funciona
 *
 * A sondagem C do G-13 mandou `commentary.append` com `delegation_id: null`
 * na delegação pendente: voltou `session.commentary.appended` e o modelo
 * **falou** (RMS 741,40, `" Ainda está em andamento, sigo acompanhando aqui."`).
 * O conteúdo enviado foi `"A consulta ainda esta em andamento, sem resultado
 * ate o momento."` — e a primeira frase daqui é ele, com os acentos que o
 * script da sonda omitiu por segurança de shell. O modelo REFORMULA o
 * conteúdo (G-12 §6), então o que muda com os acentos é a nossa grafia, não o
 * estímulo.
 *
 * ## ★ Por que estas frases não quebram a D-13/N16
 *
 * Elas dizem **o que está acontecendo**, nunca o que voltou: nenhuma carrega
 * número, cliente, ativo, período ou data-base, e — como as `SAIDA_*` — são
 * constantes CONGELADAS sem nenhuma interpolação. É por construção que dado de
 * negócio não atravessa: não existe aqui ponto onde um resultado pudesse ser
 * concatenado. Falar "durante" é dizer o que se está fazendo — o valor segue
 * indo ao CARTÃO, e a voz o remete quando ele chega.
 *
 * ⚠️ O tipo que importa é o `commentary.append`: medido no G-13, ele é o que o
 * modelo **verbaliza por treino**. `thinking.append` e `instructions.append`
 * alimentam raciocínio e comportamento, não viram fala.
 */
export const FALA_DE_ESPERA_INICIAL =
  'A consulta ainda está em andamento, sem resultado até o momento.'

export const FALA_DE_ESPERA_SEGUINTE =
  'A consulta continua em andamento e você segue acompanhando.'

export const FALA_DE_ESPERA_LONGA =
  'A consulta está demorando mais que o normal e continua em andamento.'

/**
 * A ordem É a da fala: a 1ª vai no primeiro acompanhamento, a 2ª no segundo, e
 * a 3ª em todos os seguintes. Repetir a mesma frase a cada 13 s faria a voz
 * soar travada — e uma espera que passa do terceiro aviso é uma espera longa,
 * que é um fato diferente e merece ser dito.
 */
export const FALAS_DE_ESPERA = Object.freeze([
  FALA_DE_ESPERA_INICIAL,
  FALA_DE_ESPERA_SEGUINTE,
  FALA_DE_ESPERA_LONGA,
])

/**
 * ★ [T073] As falas do DESFECHO — o segundo tempo da delegação.
 *
 * Com o laço fechado cedo, não há mais `function_call_output` por onde dizer o
 * desfecho: ele sai por `commentary.append`, e todo append passa pela guarda
 * estrutural. Estas três são as remissões `SAIDA_*` em forma de FALA — mesma
 * disciplina: congeladas, sem interpolação, sem dado de negócio.
 *
 * ⚠️ A resposta CONSOLIDADA não está aqui de propósito: ela carrega o texto da
 * tela e por isso é conferida por RE-MONTAGEM a partir da origem
 * (`conferirAFalaDeConsolidacao`), nunca por lista branca. Lista branca só
 * serve a frase que não varia.
 */
export const FALA_DE_DESFECHO_NO_CARTAO =
  'Terminei a consulta e o resultado está no cartão desta conversa.'

export const FALA_DE_DESFECHO_COM_FALHA =
  'Não consegui concluir a consulta agora. Tente de novo em instantes.'

export const FALA_DE_DECISAO_NA_TELA =
  'Preparei a alteração e ela está na tela, esperando a sua confirmação.'

/**
 * ★ [T073] As RECUSAS também passam a falar por append — e pela mesma razão
 * das outras: com o laço fechado cedo, ninguém mais pode pedir turno
 * (`response.create` abre delegação, e delegação aberta engole o append do
 * desfecho que está por vir).
 */
export const FALA_DE_CONSULTA_EM_ANDAMENTO =
  'Ainda estou com a consulta anterior. Assim que ela responder, eu faço a sua.'

export const FALA_PEDINDO_REPETIR =
  'Não entendi bem o pedido. Pode repetir, dizendo o nome do cliente?'

export const FALAS_DE_DESFECHO = Object.freeze([
  FALA_DE_DESFECHO_NO_CARTAO,
  FALA_DE_DESFECHO_COM_FALHA,
  FALA_DE_DECISAO_NA_TELA,
  FALA_DE_CONSULTA_EM_ANDAMENTO,
  FALA_PEDINDO_REPETIR,
])

export const FALAS_PERMITIDAS = Object.freeze(
  FALAS_DE_ESPERA.concat(FALAS_DE_DESFECHO)
)

/**
 * ★ [T073] A saída do turno → a FALA que a anuncia, com o laço já fechado.
 *
 * Ponto ÚNICO do mapeamento: sem ele, cada chamador escolheria a frase e a
 * lista branca viraria decoração. A ordem é a do risco — a consolidação (que
 * carrega texto) é reconhecida pela chave que só ela tem, e tudo o que não for
 * reconhecido cai na remissão ao cartão, nunca em silêncio.
 *
 * @param {Object} saida a remissão que iria ao `function_call_output`
 * @returns {string} o conteúdo do append
 */
export const falaDoDesfecho = (saida) => {
  const ehObjeto = Boolean(saida) && typeof saida === 'object'
  if (
    ehObjeto &&
    Object.prototype.hasOwnProperty.call(saida, CHAVE_DA_RESPOSTA_NA_TELA)
  ) {
    // Montada a partir da SAÍDA; conferida a partir da ORIGEM. As duas pontas
    // têm de dar no mesmo texto — é isso que a guarda verifica.
    const numero = String(saida[CHAVE_DO_VALOR_FALADO] || '')
    return (
      PREFIXO_DA_FALA_DE_DESFECHO +
      String(saida[CHAVE_DA_RESPOSTA_NA_TELA] || '') +
      (numero ? clausulaDoNumeroFalado(numero) : '')
    )
  }
  // Valor sem texto (D-O-3): a fala do número já tem forma própria e guarda
  // própria (`_conferirAFalaComValor`) — reusá-la é o que impede uma segunda
  // redação do mesmo fato.
  if (ehObjeto && Object.prototype.hasOwnProperty.call(saida, CHAVE_DO_VALOR_FALADO)) {
    return montarFalaComValor({ valorFalado: String(saida[CHAVE_DO_VALOR_FALADO]) })
  }
  if (saida === SAIDA_DECISAO_NA_TELA) return FALA_DE_DECISAO_NA_TELA
  if (saida === SAIDA_DE_FALHA) return FALA_DE_DESFECHO_COM_FALHA
  if (saida === SAIDA_DE_RECUSA_POR_CONCORRENCIA) {
    return FALA_DE_CONSULTA_EM_ANDAMENTO
  }
  // As duas recusas por não saber O QUE buscar pedem a mesma coisa: repetir
  // com o nome do cliente. Uma frase só, porque duas redações do mesmo pedido
  // divergiriam no primeiro ajuste de qualquer uma delas.
  if (saida === SAIDA_SEM_TURNO || saida === SAIDA_PEDE_IDENTIFICADOR) {
    return FALA_PEDINDO_REPETIR
  }
  return FALA_DE_DESFECHO_NO_CARTAO
}

/**
 * ★ [T063 — correção do owner, 17/09/2026] **A voz PODE adiantar o número,
 * desde que ele já esteja em mãos.**
 *
 * Palavras do owner: *"pode até adiantar número se já tiver isso (ou seja, se
 * a resposta já estiver nas mãos do agente)"*. O caso é real e não é teórico:
 * `lerDesfechoDoTurnoFalado` lê o valor do **cartão** que o turno produziu, e
 * o cartão entra no store quando a tool dele fecha — **antes** de o turno
 * inteiro acabar. Ou seja, existe um instante em que o número já está na tela
 * do consultor e a voz ainda está dizendo "estou consultando". É esse instante
 * que esta fala existe para preencher.
 *
 * ## O que a permissão NÃO afrouxa
 *
 * | invariante | por que continua |
 * |---|---|
 * | **D-O-3** | o número falado é EXATAMENTE a saída de `formatarParaFala`; quem arredonda somos nós. Medido: o modelo sozinho disse "aproximadamente 1,3 milhão" em 2 de 4 execuções da MESMA entrada — intermitência não é auditável |
 * | **N16/D-13** | o valor EXATO, o cliente, o ativo, o período e a data-base continuam no CARTÃO. O G-14 mediu o áudio divergindo do próprio transcript em 2 de 31 sessões (4,2 → 4,4 num percentual): arredondar ENCOLHE o dano, a tela é que é a fonte auditável |
 * | **N21** | identificador não entra aqui — nem o que o consultor falou, nem o que o roteador deformou |
 *
 * Nada de estimar, antecipar ou derivar: sem valor legível em mãos, esta fala
 * não é montada e a voz diz o estado, como antes.
 */
export const montarFalaComValor = ({ valorFalado }) =>
  `O resultado já chegou: ${String(valorFalado)}. ` +
  'O valor exato e o detalhe estão no cartão desta conversa.'

/**
 * ★ A MESMA verificação da D-O-3 do `function_call_output`, aplicada à fala
 * empurrada: refaz a conta a partir da origem e exige igualdade byte a byte.
 *
 * É o que impede a mudança natural de quem receber "faz a voz avisar o valor"
 * — interpolar o resultado numa frase à mão. Depois desta correção há UM jeito
 * autorizado (`montarFalaComValor`), e qualquer outro continua vermelho.
 */
const _conferirAFalaComValor = (conteudo, origem) => {
  const pedido = origem || {}
  const falado = formatarParaFala(pedido.valor, { tipo: pedido.tipo })
  if (falado && conteudo === montarFalaComValor({ valorFalado: falado })) return
  throw new ErroDeVozTempoReal(
    MOTIVO_SAIDA_RECITA_DADO,
    'A fala empurrada carrega um número que não é o que o formatador de 3 ' +
      'dígitos significativos produz para o valor em mãos: quem arredonda ' +
      'somos nós, e sem valor em mãos não se adianta número nenhum.'
  )
}

/**
 * ★ [T073] A fala é a consolidação do turno? Predicado, não guarda: quem
 * LEVANTA é `conferirAFalaDeConsolidacao`, e aqui só se pergunta qual das duas
 * réguas com origem se aplica — ver a ordem em `conferirQueAFalaRemete`.
 */
const _ehFalaDeConsolidacao = (conteudo, origem) => {
  try {
    conferirAFalaDeConsolidacao(conteudo, origem)
    return true
  } catch (erro) {
    return false
  }
}

const _FALAS_SERIALIZADAS = FALAS_PERMITIDAS.map((fala) => String(fala))

/**
 * ★ [T033] A guarda de D-13 aplicada a TODA mensagem que sai para o fio, no
 * ponto único de saída do transporte.
 *
 * `conferirQueRemete` trava o `function_call_output` — mas ele não é o único
 * caminho até a boca do modelo. `montarAppendDeFala` existe e hoje não tem
 * chamador; a mudança natural que um dev futuro faz quando lhe pedem "faz a voz
 * avisar o valor" é empurrar um `commentary.append` com o texto interpolado, e
 * aí a guarda da lista branca não estaria no caminho. Por isso a régua é
 * ESTRUTURAL: não existe rota até a fala que não passe por aqui.
 *
 * Mensagem de outro tipo passa direto — este módulo julga FALA, não protocolo.
 *
 * ★ [correção do owner] **Dois regimes, exatamente como `conferirQueRemete`**:
 * com `origem` (`{valor, tipo}` já em mãos), a fala é conferida contra o
 * FORMATADOR; sem ela, contra a lista branca. A simetria é de propósito — são
 * os dois caminhos até a boca do modelo, e um invariante que valesse só num
 * deles seria a porta que o outro deixa aberta.
 *
 * @param {Object} mensagem a que vai ao data channel
 * @param {Object} [origem] `{valor, tipo}` — o que a consulta JÁ devolveu.
 *   Sem ela não se adianta número nenhum.
 * @throws {ErroDeVozTempoReal} `MOTIVO_SAIDA_RECITA_DADO`
 */
export const conferirQueAFalaRemete = (mensagem, origem) => {
  const tipo = (mensagem && mensagem.type) || ''
  if (tipo !== EVENTO_APPEND_DE_COMENTARIO) return
  const conteudo = String((mensagem && mensagem.content) || '')
  if (_FALAS_SERIALIZADAS.indexOf(conteudo) !== -1) return
  if (origem) {
    // ★ [T073] Duas falas com origem, e a ordem importa: a CONSOLIDAÇÃO (o
    // texto da tela, D-O-6) é o caso do desfecho em dois tempos; o VALOR
    // adiantado (T063) é o do meio da espera. A primeira é tentada antes
    // porque é a que carrega texto — cair na segunda com um texto na mão
    // produziria a mensagem de erro errada, falando de arredondamento.
    if (_ehFalaDeConsolidacao(conteudo, origem)) return
    _conferirAFalaComValor(conteudo, origem)
    return
  }
  throw new ErroDeVozTempoReal(
    MOTIVO_SAIDA_RECITA_DADO,
    'Fala empurrada ao canal fora da lista de frases aprovadas: o dado de ' +
      'negócio vai ao cartão, não à fala.'
  )
}
